import React, { useEffect, useRef, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { Box, Center, Loader, Text, Button, Stack } from '@mantine/core';
import { IconFileText, IconExternalLink } from '@tabler/icons-react';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// Worker de pdf.js servido desde public/ (mismo origen). Usamos extensión .js
// (no .mjs) porque nginx en producción no tiene .mjs en mime.types y lo serviría
// como application/octet-stream, lo que impide cargarlo como módulo del worker.
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

interface PdfViewerProps {
  url: string;
  dark?: boolean;
}

/**
 * Visor de PDF basado en react-pdf (pdf.js). Renderiza las páginas como
 * elementos HTML apilados, de modo que el scroll táctil funciona en todos los
 * dispositivos (a diferencia de un <iframe>, que en Safari iOS no deja
 * desplazar el PDF con el dedo).
 */
const PdfViewer: React.FC<PdfViewerProps> = ({ url, dark }) => {
  const [numPages, setNumPages] = useState(0);
  const [error, setError] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState<number>(0);

  // Ajustar el ancho de las páginas al del contenedor (responsive).
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () => setWidth(el.clientWidth);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  if (error) {
    return (
      <Center style={{ height: '100%' }}>
        <Stack align="center" gap="sm">
          <IconFileText size={40} color={dark ? '#94a3b8' : '#64748b'} />
          <Text size="sm" style={{ color: dark ? '#94a3b8' : '#64748b' }}>
            No se pudo mostrar el PDF aquí.
          </Text>
          <Button
            component="a"
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            leftSection={<IconExternalLink size={16} />}
            variant="light"
          >
            Abrir PDF
          </Button>
        </Stack>
      </Center>
    );
  }

  return (
    <Box
      ref={containerRef}
      data-pdf-viewer
      style={{
        flex: 1,
        height: '100%',
        overflowY: 'auto',
        WebkitOverflowScrolling: 'touch',
        background: dark ? '#0f172a' : '#e5e7eb',
        padding: '12px 0',
      }}
    >
      <Document
        file={url}
        onLoadSuccess={({ numPages }) => setNumPages(numPages)}
        onLoadError={() => setError(true)}
        loading={
          <Center style={{ height: '60vh' }}>
            <Loader color={dark ? 'gray' : 'dark'} />
          </Center>
        }
      >
        {Array.from({ length: numPages }, (_, i) => (
          <Center key={`page-${i + 1}`} style={{ marginBottom: 12 }}>
            {width > 0 && (
              <Page
                pageNumber={i + 1}
                width={Math.min(width - 24, 900)}
                renderAnnotationLayer={false}
                renderTextLayer={false}
                loading=""
              />
            )}
          </Center>
        ))}
      </Document>
    </Box>
  );
};

export default PdfViewer;
